/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import get from 'lodash/get';

/*

  Async helper functions to reduce the amount of code to write to implement actions
  and reducers that do asynchronous work.

  All async data is combined with loading and loaded tracking, error handling.

 */

/**
 * Builds a PropType.shape the async action and reducers use
 * @param {PropTypes} propType - a React PropTypes object
 * @return {PropTypes} - a PropTypes.shape for async actions and reducers
 */
export function asyncProps(propType) {
    return PropTypes.shape({
        loading: PropTypes.bool,
        loaded: PropTypes.bool,
        error: PropTypes.string,
        data: propType,
    });
}

/**
 * Builds an object that represents an async helper object containing initial state data
 * @param {*} data
 * @return {{loading: boolean, loaded: boolean, error: null, data: *}}
 */
export function asyncDefaults(data) {
    return {
        loading: false,
        loaded: false,
        error: null,
        data,
    };
}

// Default action step functions
// Function signature (dispatch, { getState, type, key }) => { return <action> }
const startActionFn = (dispatch, { type }) => dispatch({ type });
// Function signature (dispatch, { getState, payload, type, key }) => { return <action> }
const successActionFn = (dispatch, { payload, type }) => dispatch({ type, payload });
// Function signature (dispatch, { getState, error, message, type, key }) => { return <action> }
const failureActionFn = (dispatch, { message, type }) =>
    dispatch({ type, error: message });

/**
 * Wraps an asynchronous task with distinct reducer steps. Should be paired with makeAsyncReducer
 * @param {string} type - action type prefix
 * @param {Function} thunkCallback - a function that returns function that returns a promise
 * @param {string} [key=undefined] - key or object path to value (uses lodash get)
 * @param {Object} [functions]
 * @param {Function} [functions.start=startActionFn]
 * @param {Function} [functions.success=successActionFn]
 * @param {Function} [functions.failure=failureActionFn]
 * @throws {Error} Throws an error when receiving bad data from the async function
 * @return {function(...[*]): Function} redux action function
 */
export function makeAsyncAction(
    type,
    thunkCallback,
    key = undefined,
    { start = startActionFn, success = successActionFn, failure = failureActionFn } = {}
) {
    return (...args) => {
        const thunk = thunkCallback(...args);

        return async (dispatch, getState) => {
            start(dispatch, { getState, type: `${type}_START`, key });

            try {
                const response = await thunk();
                const payload =
                    key !== undefined ? get(response, `data.${key}`) : response.data;

                if (payload === undefined) {
                    throw new Error(`Did not receive data from the server.`);
                }

                success(dispatch, { getState, payload, type: `${type}_SUCCESS`, key });

                return payload;
            } catch (error) {
                let message;

                if (error.response) {
                    // Handle 5xx errors
                    const response = error.response;
                    if (response.status >= 500) {
                        switch (response.status) {
                            case 500: // Internal Server Error
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message =
                                        'The server encountered an error processing the request. Try again later.';
                                }
                                break;
                            case 501: // Not Implemented
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message =
                                        'The server does not know how to respond to that request.';
                                }
                                break;
                            case 502: // Bad Gateway
                            case 503: // Service Unavailable
                            case 504: // Gateway Timeout
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message =
                                        'The server could not be reached or is not responding. Try again later.';
                                }
                                break;
                            default:
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message =
                                        'Something went wrong while talking with the server. Try again later.';
                                }
                        }
                    } else if (response.status >= 400) {
                        switch (response.status) {
                            case 401: // Unauthorized (not authenticated)
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message = 'You must be logged in to do that action.';
                                }
                                break;
                            case 403: // Forbidden (not authorized)
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message =
                                        'Your permissions do not permit you to do that action.';
                                }
                                break;
                            case 404: // Not Found
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message = 'No data for that resource was found.';
                                }
                                break;
                            case 422: // Unprocessable Entity
                                // if (response.data && (response.data.err || response.data.error)) {
                                // BAD ParkPassService! It commingles the errors with the valid reservations, instead of living inside err.
                                // Make up for this here.
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else if (response.data && response.data.reservations) {
                                    message = response.data.reservations;
                                } else {
                                    message =
                                        'There are errors in your request, check your inputs and try again.';
                                }
                                break;
                            default:
                                if (
                                    response.data &&
                                    (response.data.err || response.data.error)
                                ) {
                                    message = response.data.err || response.data.error;
                                } else {
                                    message =
                                        'A problem with your request has occurred. Try again later.';
                                }
                        }
                    } else if (
                        response.data &&
                        (response.data.err || response.data.error)
                    ) {
                        message = response.data.err || response.data.error;
                    } else {
                        message =
                            'A temporary problem has caused your request to fail. Try again later.';
                    }
                }
                // Error with the request (no server response)
                else if (error.request) {
                    message = 'Something went wrong while talking to the server';
                }
                // Raw Axios or other error describing the problem (technical) || Unknown failure
                else {
                    message = error.message || 'Something went wrong unexpectedly';
                }

                failure(dispatch, {
                    getState,
                    error,
                    message,
                    type: `${type}_FAILURE`,
                    key,
                });

                // Can be caught with try {} catch (error) , or is using Promise syntax with .catch()
                throw new Error(message);
            }
        };
    };
}

/**
 * Creates a simple action to reset the async helper state back to initial data
 * @param {string} type - action type
 * @return {function(): function(*): *} redux action function
 */
export function makeAsyncResetAction(type) {
    return () => (dispatch) => dispatch({ type });
}

// Common async reducer functions, a success which will populate data coming from the server on success but
// allows replacement an example: success can be replaced in makeAsyncReducer to instead reset to the initial
// data on a delete instead.
// Function signatures (state, action, initial) => { return <state> }
const startReducerFn = (state) => ({
    loading: true,
    loaded: false,
    error: null,
    data: state.data,
});
const successReducerFn = (state, action) => ({
    loading: false,
    loaded: true,
    error: null,
    data: action.payload,
});
const failureReducerFn = (state, action) => ({
    loading: false,
    loaded: true,
    error: action.error,
    data: state.data,
});

/**
 * Creates a reducer to handle all makeAsyncAction action steps
 * @param {string} type - action type prefix
 * @param {Object} [options]
 * @param {*} [options.initialData=null] - the initial data to use
 * @param {Function} [options.start=startReducerFn]
 * @param {Function} [options.success=successReducerFn]
 * @param {Function} [options.failure=failureReducerFn]
 * @return {function(state, action): *} redux reducer function
 */
export function makeAsyncReducer(
    type,
    {
        initialData = null,
        start = startReducerFn,
        success = successReducerFn,
        failure = failureReducerFn,
    } = {}
) {
    const initial = { loading: false, loaded: false, error: null, data: initialData };

    return (state = initial, action) => {
        switch (action.type) {
            case `${type}_START`:
                return start(state, action, initialData);
            case `${type}_SUCCESS`:
                return success(state, action, initialData);
            case `${type}_FAILURE`:
                return failure(state, action, initialData);
            default:
                return state;
        }
    };
}

/**
 * Creates a simple reducer to reset the async helper state back to initial data
 * @param {string} type - action type
 * @param {*} [initialData=null]
 * @return {function(state, action): *} redux reducer function
 */
export function makeAsyncResetReducer(type, initialData = null) {
    return (state, action) => {
        if (action.type === type) {
            return asyncDefaults(initialData);
        }

        return state;
    };
}

/**
 * Gates all related reducers behind a prefix check and returns a reducing reducer
 * @param {*} initialState - initial async state to use for all reducers
 * @param {string} prefix - action prefix
 * @param {...Function} reducers
 * @return {function(state, action): *} redux reducer function
 */
export function mergeReducers(initialState, prefix, ...reducers) {
    return (initial = initialState, action) => {
        if (typeof action.type === 'undefined' || !action.type.startsWith(prefix)) {
            return initial;
        }

        return reducers.reduce((state, reducer) => reducer(state, action), initial);
    };
}

export const STATE_UNKNOWN = -1;
export const STATE_UNLOADED = 0;
export const STATE_LOADING = 1;
export const STATE_READY = 2;
export const STATE_ERROR = 3;

/**
 * Makes some async selector helpers for $reducer
 *
 * isLoading() Returns an object of booleans. Any and All are included for quick checks across all keys
 * isLoaded() Returns an object of booleans. Any and All are included for quick checks across all keys
 * hasErrors() Returns an object of booleans. Any and All are included for quick checks across all keys
 * getErrors() Returns an object of error messages for only the keys that have errors.
 *
 * @param {string} reducer - name of UI reducer found on the root state object
 * @return {Object} object of selector functions
 */
export function makeAsyncSelectors(reducer) {
    return {
        isLoading(state, ...keys) {
            const result = {};
            let all = true;
            let any = false;

            keys.forEach((key) => {
                all =
                    all &&
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].loading;
                any =
                    any ||
                    (state[reducer] &&
                        state[reducer][key] &&
                        state[reducer][key].loading);
                result[key] =
                    state[reducer] && state[reducer][key] && state[reducer][key].loading;
            });

            result.all = all;
            result.any = any;

            return result;
        },

        isLoaded(state, ...keys) {
            const result = {};
            let all = true;
            let any = false;

            keys.forEach((key) => {
                all =
                    all &&
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].loaded;
                any =
                    any ||
                    (state[reducer] && state[reducer][key] && state[reducer][key].loaded);
                result[key] =
                    state[reducer] && state[reducer][key] && state[reducer][key].loaded;
            });

            result.all = all;
            result.any = any;

            return result;
        },

        getState(state, ...keys) {
            const result = {};

            keys.forEach((key) => {
                if (!state[reducer] || !state[reducer][key]) {
                    result[key] = STATE_UNKNOWN;
                } else {
                    const value = state[reducer][key];
                    if (value.error) {
                        result[key] = STATE_ERROR;
                    } else if (value.loading) {
                        result[key] = STATE_LOADING;
                    } else if (value.loaded) {
                        result[key] = STATE_READY;
                    } else {
                        result[key] = STATE_UNLOADED;
                    }
                }
            });

            return result;
        },

        hasErrors(state, ...keys) {
            const result = {};
            let all = true;
            let any = false;

            keys.forEach((key) => {
                all =
                    all &&
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].error !== null;
                any =
                    any ||
                    (state[reducer] &&
                        state[reducer][key] &&
                        state[reducer][key].error !== null);
                result[key] =
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].error !== null;
            });

            result.all = all;
            result.any = any;

            return result;
        },

        getErrors(state, ...keys) {
            const result = {};

            keys.forEach((key) => {
                if (state[reducer][key].error !== null) {
                    result[key] =
                        state[reducer] &&
                        state[reducer][key] &&
                        state[reducer][key].error;
                }
            });

            return result;
        },
    };
}
